// Helper to compute detailed Reuse (human) footprint metrics across indicators
// Based on the "Food Donation/Redistribution" methodology described in
// backend/Documantation/foodcycle.ai Emissions Reporting Methodology_AIE_05.02.2025.docx

import {
  substitutionRate as SR,
  donationEfficiency as FWDE,
  ewtCo2ePerKg,
  ewtWaterM3PerKg,
  ewtLandPerKg,
  ateCo2ePerTkm,
  ateWaterPerTkm,
  ateLandPerTkm,
  toastBreadCo2ePerKg,
  toastBreadWaterM3PerKg,
  toastBreadLandPerKg,
} from '@/constants/ef31';

function safeNum(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) {
    return 0;
  }
  return Math.abs(n) < 1e-12 ? 0 : n;
}

function getUpToPOS(impacts, key) {
  const group = (impacts && impacts[key]) || {};
  const total = safeNum(group.total);
  const consumption = safeNum(group.consumption);
  const diff = total - consumption;
  return diff > 0 ? diff : 0;
}

function buildIndicatorMetrics({
  productUpToPosPerKg,
  toastReferencePerKg,
  wasteTreatmentPerKg,
  transportPerTkm,
  massKg,
  tonneKm,
}) {
  const prod = safeNum(productUpToPosPerKg);
  const toast = safeNum(toastReferencePerKg);
  const waste = safeNum(wasteTreatmentPerKg);
  const transport = safeNum(transportPerTkm);
  const M = safeNum(massKg);
  const tk = safeNum(tonneKm);

  const baseline = (prod + waste) * M;
  const productionSubstitution = toast * SR * M;
  const wasteAvoided = waste * FWDE * M;
  const transportAdded = transport * tk;
  const netEmissions = baseline - productionSubstitution - wasteAvoided + transportAdded;
  const relativeReduction = baseline - netEmissions;

  return {
    productUpToPosPerKg: prod,
    toastReferencePerKg: toast,
    wasteTreatmentPerKg: waste,
    transportPerTkm: transport,
    baseline,
    productionSubstitution,
    wasteAvoided,
    transportAdded,
    netEmissions,
    relativeReduction,
  };
}

export function calculateReuseHumanMetrics({ impacts, massKg, distanceKm, transportMassKg }) {
  const impactsSafe = impacts || {};
  const M = safeNum(massKg);
  const D = safeNum(distanceKm);
  const transportMass = safeNum(transportMassKg);
  const effectiveTransportMass = transportMass > 0 ? transportMass : M;
  const tonneKm = (effectiveTransportMass / 1000) * D;

  const co2Metrics = buildIndicatorMetrics({
    productUpToPosPerKg: getUpToPOS(impactsSafe, 'climate_change'),
    toastReferencePerKg: toastBreadCo2ePerKg,
    wasteTreatmentPerKg: ewtCo2ePerKg,
    transportPerTkm: ateCo2ePerTkm,
    massKg: M,
    tonneKm,
  });

  const waterMetrics = buildIndicatorMetrics({
    productUpToPosPerKg: getUpToPOS(impactsSafe, 'water_resource_depletion'),
    toastReferencePerKg: toastBreadWaterM3PerKg,
    wasteTreatmentPerKg: ewtWaterM3PerKg,
    transportPerTkm: ateWaterPerTkm,
    massKg: M,
    tonneKm,
  });

  const landMetrics = buildIndicatorMetrics({
    productUpToPosPerKg: getUpToPOS(impactsSafe, 'land_use'),
    toastReferencePerKg: toastBreadLandPerKg,
    wasteTreatmentPerKg: ewtLandPerKg,
    transportPerTkm: ateLandPerTkm,
    massKg: M,
    tonneKm,
  });

  return {
    massKg: M,
    transportMassKg: effectiveTransportMass,
    distanceKm: D,
    tonneKm,
    constants: {
      substitutionRate: SR,
      donationEfficiency: FWDE,
      ateCo2ePerTkm,
    },
    indicators: {
      co2: co2Metrics,
      water: waterMetrics,
      land: landMetrics,
    },
  };
}

export function buildEmptyReuseHumanMetrics() {
  return {
    massKg: 0,
    transportMassKg: 0,
    distanceKm: 0,
    tonneKm: 0,
    constants: {
      substitutionRate: SR,
      donationEfficiency: FWDE,
      ateCo2ePerTkm,
    },
    indicators: {
      co2: buildIndicatorMetrics({
        productUpToPosPerKg: 0,
        toastReferencePerKg: 0,
        wasteTreatmentPerKg: 0,
        transportPerTkm: ateCo2ePerTkm,
        massKg: 0,
        tonneKm: 0,
      }),
      water: buildIndicatorMetrics({
        productUpToPosPerKg: 0,
        toastReferencePerKg: 0,
        wasteTreatmentPerKg: 0,
        transportPerTkm: ateWaterPerTkm,
        massKg: 0,
        tonneKm: 0,
      }),
      land: buildIndicatorMetrics({
        productUpToPosPerKg: 0,
        toastReferencePerKg: 0,
        wasteTreatmentPerKg: 0,
        transportPerTkm: ateLandPerTkm,
        massKg: 0,
        tonneKm: 0,
      }),
    },
  };
}
